/**
 * Reads the session the new family app (v2, served at `/v2` on this origin)
 * persists, so a user who signed in over there is not asked to sign in again
 * after the cut-over gate hands a screen back to this app.
 *
 * The two apps speak the same header protocol to the same services, so the
 * tokens are interchangeable; only the storage differs. v2 uses zustand's
 * persist middleware, which writes `{ state, version }` under
 * `family-app:session`, with the session store's partialize output as
 * `state`. That shape is v2-internal — a store upgrade over there needs a
 * matching change here, until both apps move to one shared key.
 *
 * It also writes this app's token rotations back into that key, because a
 * rotation here revokes the pair the new app is holding.
 *
 * Deliberately import-free so it can be unit-tested from a node environment.
 */

export const V2_SESSION_KEY = "family-app:session";

export type V2Session = {
  id?: string;
  role?: string;
  userId: string;
  clientId?: string;
  familyId?: string;
  accessToken: string;
  refreshToken: string;
};

/**
 * The fields a repair may fill. Tokens are deliberately not among them: the
 * slice's pair is the live one, and the claims only describe it.
 */
export type AuthRepairPatch = {
  id?: string;
  role?: string;
  userId?: string;
  clientId?: string;
  familyId?: string;
};

/** The subset of the access token's payload this app has a use for. */
export type SessionClaims = {
  id?: string;
  role?: string;
  userId?: string;
  clientId?: string;
  familyId?: string;
};

type V1Tokens = { accessToken?: string; refreshToken?: string };

/**
 * The tokens held by the new app, or null. Only an `active` session is
 * adopted: `pending` means a registration is still mid-flight over there, so
 * the account exists but the profile the app stack assumes does not.
 */
export function readV2Session(raw: string | null): V2Session | null {
  const state = readState(raw);

  if (!state || state.status !== "active") {
    return null;
  }

  const session = state.session;

  if (
    !session ||
    typeof session !== "object" ||
    typeof session.userId !== "string" ||
    typeof session.accessToken !== "string" ||
    typeof session.refreshToken !== "string"
  ) {
    return null;
  }

  return {
    id: typeof session.id === "string" ? session.id : undefined,
    role: typeof session.role === "string" ? session.role : undefined,
    userId: session.userId,
    clientId:
      typeof session.clientId === "string" ? session.clientId : undefined,
    familyId:
      typeof session.familyId === "string" ? session.familyId : undefined,
    accessToken: session.accessToken,
    refreshToken: session.refreshToken,
  };
}

/**
 * The claims carried by an access token, or null if it is not a readable JWT.
 *
 * The new app stores only what its own screens need, and `clientId` is not
 * among them — but the first screen this app shows after a hop asks the
 * backend for `/v1/family/:familyId/client?id=<clientId>`, and without it
 * sends the string "undefined". auth-service signs the id into the token, so
 * it is already in the browser.
 *
 * Nothing is verified here and nothing needs to be: the signature is checked
 * by the server on every request this token is used for. This only reads an
 * identifier out of a credential the browser is already holding, and a token
 * that cannot be read yields nothing rather than a guess.
 */
export function readSessionClaims(accessToken: string): SessionClaims | null {
  const segments = (accessToken ?? "").split(".");

  if (segments.length !== 3) {
    return null;
  }

  const payload = decodePayload(segments[1]);

  if (!payload) {
    return null;
  }

  return {
    id: readClaim(payload.id),
    role: readClaim(payload.role),
    userId: readClaim(payload.userId),
    clientId: readClaim(payload.clientId),
    familyId: readClaim(payload.familyId),
  };
}

const readClaim = (value: unknown) =>
  typeof value === "string" ? value : undefined;

/**
 * What the token can tell a session that is already in the store, and which
 * that session does not already know.
 *
 * A session adopted by an older bundle never gets adopted again — the
 * adoption only ever reads the other app's key, and by then this app has its
 * own tokens — so the hole it was written with is permanent unless the store
 * can repair itself. CloudFront serves index.html for up to ten minutes, so
 * "the fix is deployed" and "every tab is running it" are never the same
 * moment.
 *
 * An empty string is a hole, not an answer: the adoption writes
 * `id: session.id ?? ""`. And a field the slice already has is left exactly
 * as it is — this fills gaps, it does not reconcile.
 */
export function missingAuthFields(
  auth: AuthRepairPatch,
  claims: SessionClaims | null,
): AuthRepairPatch {
  if (!claims) {
    return {};
  }

  const patch: AuthRepairPatch = {};

  (["id", "role", "userId", "clientId", "familyId"] as const).forEach((key) => {
    const claimed = claims[key];

    if (!auth[key] && claimed) {
      patch[key] = claimed;
    }
  });

  return patch;
}

function decodePayload(segment: string): Record<string, unknown> | null {
  // base64url for the two characters standard base64 spells differently, and
  // the "=" padding it drops has to be put back before atob will look at it.
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    "=",
  );

  try {
    const decoded = JSON.parse(atob(padded));

    return decoded && typeof decoded === "object" && !Array.isArray(decoded)
      ? (decoded as Record<string, unknown>)
      : null;
  } catch {
    // Not base64, not JSON, or not this app's business. Either way there is
    // nothing to read, and nothing is what the caller gets.
    return null;
  }
}

/**
 * The session to adopt on boot, or null to leave things as they are. This
 * app's own session always wins — adopting over it would swap the user
 * mid-visit if two accounts had been used in the same browser.
 */
export function adoptableV2Session(
  tokens: V1Tokens,
  raw: string | null = readStoredV2Session(),
): V2Session | null {
  if (tokens.accessToken && tokens.refreshToken) {
    return null;
  }

  const session = readV2Session(raw);

  if (!session) {
    return null;
  }

  // The envelope is what the other app is actually running on, so it wins
  // wherever it has an answer; the token only fills the gaps it leaves.
  const claims = readSessionClaims(session.accessToken);

  return claims
    ? {
        ...session,
        id: session.id ?? claims.id,
        role: session.role ?? claims.role,
        clientId: session.clientId ?? claims.clientId,
        familyId: session.familyId ?? claims.familyId,
      }
    : session;
}

export function readStoredV2Session(): string | null {
  try {
    return window.localStorage.getItem(V2_SESSION_KEY);
  } catch {
    // Storage can be unavailable (private mode, blocked site data). Then
    // there is nothing to adopt, which is the same as nothing being stored.
    return null;
  }
}

export type V2TokenPair = {
  userId: string;
  accessToken: string;
  refreshToken: string;
};

/**
 * The new app's stored envelope with this app's current pair written into
 * it, or null to leave the key exactly as it is.
 *
 * Refresh tokens are single-use: once this app rotates, the pair the new app
 * holds is dead, and the new app always boots on its own key rather than
 * re-adopting this one. Without the write-back, the next hop to `/v2` boots
 * on a revoked pair, 401s, and the clear that follows is mirrored into this
 * app's storage too — the family is logged out of both.
 *
 * Only the two tokens change. Everything else in the envelope — the version,
 * the status, the draft, the session's other fields — is carried over as
 * found. Nothing is written for a key that is missing or unreadable (there
 * is no session over there to keep alive), for a session that belongs to
 * somebody else, or for a registration still pending over there, whose
 * tokens the new app deliberately keeps to itself.
 */
export function writeV2Tokens(
  raw: string | null,
  pair: V2TokenPair,
): string | null {
  if (!raw || !pair.userId) {
    return null;
  }

  let envelope: unknown;

  try {
    envelope = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!isRecord(envelope) || !isRecord(envelope.state)) {
    return null;
  }

  const { state } = envelope;

  if (state.status === "pending" || !isRecord(state.session)) {
    return null;
  }

  const { session } = state;

  if (session.userId !== pair.userId) {
    return null;
  }

  if (
    session.accessToken === pair.accessToken &&
    session.refreshToken === pair.refreshToken
  ) {
    return null;
  }

  return JSON.stringify({
    ...envelope,
    state: {
      ...state,
      session: {
        ...session,
        accessToken: pair.accessToken,
        refreshToken: pair.refreshToken,
      },
    },
  });
}

/**
 * Writes this app's current pair into the new app's key, per writeV2Tokens.
 * Called after every token change this app makes; see v2-session-mirror.
 */
export function mirrorTokensToV2(pair: V2TokenPair) {
  try {
    const next = writeV2Tokens(
      window.localStorage.getItem(V2_SESSION_KEY),
      pair,
    );

    if (next !== null) {
      window.localStorage.setItem(V2_SESSION_KEY, next);
    }
  } catch {
    // Storage can be unavailable. The rotation itself has already landed in
    // this app's store, and a failed mirror must not undo or block it.
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Whether the new app's stored session belongs to someone other than the
 * user who just signed in here. A key with no session in it belongs to
 * nobody, and an unreadable one is left for the new app to deal with.
 */
export function heldByAnotherUser(
  raw: string | null,
  userId: string | undefined,
): boolean {
  const session = readState(raw)?.session;

  if (!session || typeof session !== "object") {
    return false;
  }

  return session.userId !== userId;
}

/**
 * A sign-in here settles who this browser belongs to. If the new app still
 * holds another person's session — a shared browser, or a registration left
 * half-finished by someone else — the next hop to `/v2` would open their
 * account, and the new app would never notice: it boots on its own key. So
 * that key goes. The same user's session stays; the rotation mirror keeps
 * its tokens in step with this app's.
 */
export function forgetAnotherUsersV2Session(userId: string | undefined) {
  try {
    if (
      heldByAnotherUser(window.localStorage.getItem(V2_SESSION_KEY), userId)
    ) {
      window.localStorage.removeItem(V2_SESSION_KEY);
    }
  } catch {
    // Storage can be unavailable, and then there is no key to hop into.
  }
}

/** Logging out of one app has to log the user out of the other one too. */
export function clearV2Session() {
  try {
    window.localStorage.removeItem(V2_SESSION_KEY);
  } catch {
    // Nothing was readable, so nothing was adopted either.
  }
}

type PersistedState = {
  status?: unknown;
  session?: {
    id?: unknown;
    role?: unknown;
    userId?: unknown;
    clientId?: unknown;
    familyId?: unknown;
    accessToken?: unknown;
    refreshToken?: unknown;
  } | null;
};

function readState(raw: string | null): PersistedState | null {
  if (!raw) {
    return null;
  }

  try {
    const envelope = JSON.parse(raw);

    if (!envelope || typeof envelope !== "object") {
      return null;
    }

    const state = (envelope as { state?: unknown }).state;

    return state && typeof state === "object"
      ? (state as PersistedState)
      : null;
  } catch {
    // A truncated or hand-edited value is not a session.
    return null;
  }
}
